/**
 * Formulario de Contratación de Candidatos
 * Fecha: 16 Oct 2025
 *
 * Wizard de 4 pasos para convertir un candidato en empleado
 */
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'
import { formatDate } from '@/shared/lib/dateUtils'
import { TituloDePantalla } from '@/shared/components/TituloDePantalla'
import { mensajesDeValidacion } from '@/features/rh/lib/mensajeDeValidacion'
import { experienciaDelCandidato } from '@/features/rh/lib/experienciaDelCandidato'

import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import SelectBuscable from '@/shared/components/SelectBuscable';
import { hiringService } from '@/features/rh/api/hiring.service';
import type { HireEmployeeDto } from '@/types/employee.types';
import { LoadingSpinner } from '@/shared/components';
import {
  CheckCircle,
  AlertCircle,
  ArrowLeft,
  ArrowRight,
} from "lucide-react";
import { useHireDraftAutosave } from '@/hooks/useHireDraftAutosave';
import { validationRules, formatters } from '@/shared/lib/formValidations';
import toast from "react-hot-toast";
import { filterOutMasterUsers } from '@/shared/lib/userFilters';
import { Modal } from '@/shared/components/Modal';
import { config } from '@/config/env'

interface Area {
  id: number;
  nombre: string;
  descripcion?: string;
  activa: boolean;
}

interface Puesto {
  id: number;
  nombre: string;
  descripcion?: string;
  areaId: number;
  activo: boolean;
}

interface Usuario {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  isActive: boolean;
}

interface Candidate {
  id: string;
  fullName: string;
  email: string;
  phone: string;
  age?: number;
  experienceYears?: number;
  experienceDescription?: string;
  cvUrl: string | null;
  status?: string;
  position?: string;
  jobRequestId?: number;
  jobRequestTipoPuesto?: string;
  jobRequestSalarioMinimo?: string | number;
  jobRequestSalarioMaximo?: string | number;
  proyecto?: {
    createdAt: string;
    description: string;
    id: string;
    name: string;
    updatedAt: string;
  };
}

interface ProjectOption {
  id: string;
  name: string;
  description?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface FormData extends HireEmployeeDto {
  // Campos solo usados en modo standalone (alta sin candidato)
  firstName: string;
  lastName: string;
  email: string;
}

const ESTADOS_CIVILES = [
  { value: "soltero", label: "Soltero/a" },
  { value: "casado", label: "Casado/a" },
  { value: "union_libre", label: "Unión Libre" },
  { value: "divorciado", label: "Divorciado/a" },
  { value: "viudo", label: "Viudo/a" },
];

const SEXOS = [
  { value: "masculino", label: "Masculino" },
  { value: "femenino", label: "Femenino" },
];

const TIPOS_CONTRATO = [
  { value: "tiempo_completo", label: "Tiempo completo" },
  { value: "medio_tiempo", label: "Medio tiempo" },
  { value: "por_proyecto", label: "Por proyecto" },
  { value: "por_horas", label: "Por horas" },
];

const JORNADAS = [
  { value: "tiempo_completo", label: "Tiempo Completo" },
  { value: "medio_tiempo", label: "Medio Tiempo" },
  { value: "por_horas", label: "Por Horas" },
  { value: "reducida", label: "Reducida" },
  { value: "mixta", label: "Mixta" },
  { value: "nocturna", label: "Nocturna" },
];

/**
 * Pares etiqueta/valor de la ficha del candidato, con el tamaño que pide el
 * diseño: 12px para el rótulo y 14px para el dato.
 */
const ETIQUETA_DE_DATO = "text-xs font-medium text-[#777777]";
const VALOR_DE_DATO = "text-sm font-normal text-[#2F2F2F] mt-0.5";

/** Encabezado de sección: 16px semibold, con su línea abajo. */
const TITULO_DE_SECCION = "text-base font-semibold text-[#1E1E1F]";

/** El título vive en el encabezado de la tarjeta, no dentro de cada paso. */
const TITULOS_DE_PASO: Record<number, string> = {
  1: "Datos personales",
  2: "Datos laborales",
  3: "Contacto de emergencia",
};

const normalizeContractTypeValue = (value?: string | null): string => {
  return (value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[\s-]+/g, "_")
    .trim();
};

const isProjectContractType = (value?: string | null): boolean =>
  normalizeContractTypeValue(value) === "por_proyecto";

export const HireForm: React.FC = () => {
  const { candidateId } = useParams<{ candidateId: string }>();
  const navigate = useNavigate();

  // Modo standalone: alta de empleado sin candidato/solicitud
  const isStandalone = !candidateId;

  const [currentStep, setCurrentStep] = useState(1); // 1: Personales, 2: Laborales, 3: Emergencia
  const [candidate, setCandidate] = useState<Candidate | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Estados para catálogos
  const [areas, setAreas] = useState<Area[]>([]);
  const [puestos, setPuestos] = useState<Puesto[]>([]); // Todos los puestos
  const [puestosFiltrados, setPuestosFiltrados] = useState<Puesto[]>([]); // Puestos filtrados por área
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string>("");
  const [loadingCatalogs, setLoadingCatalogs] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdEmployeeId, setCreatedEmployeeId] = useState<number | null>(
    null
  );
  const [temporaryPassword, setTemporaryPassword] = useState<string | null>(
    null
  );

  // Estado inicial del formulario
  const initialFormData: FormData = {
    // Datos de identidad (solo standalone)
    firstName: "",
    lastName: "",
    email: "",

    // Datos personales
    curp: "",
    rfc: "",
    nss: "",
    fechaNacimiento: "",
    sexo: "masculino" as any,
    telefonoPersonal: "",
    estadoCivil: "soltero" as any,
    direccion: "",

    // Datos laborales
    areaId: 0,
    puestoId: 0,
    fechaIngreso: "",
    tipoContrato: "tiempo_completo" as any,
    jornada: "tiempo_completo" as any,
    sueldoBrutoMensual: undefined as any,
    jefeInmediatoId: "",
    prestaciones: "",

    // Contacto emergencia
    emergenciaContactoNombre: "",
    emergenciaContactoRelacion: "",
    emergenciaContactoTelefono: "",
  };

  // React Hook Form
  const {
    register,
    control,
    handleSubmit: handleFormSubmit,
    watch,
    formState: { errors, isSubmitting },
    setValue,
    trigger,
    reset,
  } = useForm<FormData>({
    defaultValues: initialFormData,
    mode: "onChange", // Valida mientras escribe
    reValidateMode: "onChange", // Re-valida en cada cambio
  });

  const formData = watch();
  const selectedTipoContrato = watch("tipoContrato");
  const isProjectContractSelected = isProjectContractType(selectedTipoContrato);
  /**
   * Proyecto que se eligió en la solicitud de personal.
   *
   * Se muestra siempre que la solicitud lo traiga, sin importar el tipo de
   * contrato: la asignación al proyecto existe igual si el contrato es de
   * tiempo completo. Antes la sección solo aparecía con contrato por proyecto,
   * así que en los demás casos el dato quedaba invisible.
   */
  const proyectoDeLaSolicitud = candidate?.proyecto ?? null;
  const proyectoEsDeSolicitud = !isStandalone && !!proyectoDeLaSolicitud?.id;
  const mostrarDatosProyecto =
    isProjectContractSelected || !!proyectoDeLaSolicitud?.id;
  const availableContractTypes = TIPOS_CONTRATO;
  // Cuando el proyecto viene de la solicitud se usa ese directamente: no debe
  // depender de que el efecto que siembra selectedProjectId ya haya corrido.
  const selectedProject = proyectoEsDeSolicitud
    ? proyectoDeLaSolicitud
    : projects.find((project) => project.id === selectedProjectId) ||
      (candidate?.proyecto?.id === selectedProjectId ? candidate.proyecto : null);
  const toNumberOrNull = (value?: string | number): number | null => {
    if (value === undefined || value === null || value === "") return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };
  const salaryMin = toNumberOrNull(candidate?.jobRequestSalarioMinimo);
  const salaryMax = toNumberOrNull(candidate?.jobRequestSalarioMaximo);
  const formatCurrency = (value: number) =>
    value.toLocaleString("es-MX", { style: "currency", currency: "MXN" });
  const approvedSalaryRangeText =
    salaryMin !== null && salaryMax !== null
      ? `Rango aprobado: ${formatCurrency(salaryMin)} — ${formatCurrency(
          salaryMax
        )}`
      : null;

  // Hook de autoguardado con backend
  const { saveDraft, loadDraft, deleteDraft } = useHireDraftAutosave({
    candidateId: candidateId!,
    enabled: !isStandalone && currentStep < 3,
    debounceMs: 5000,
    // onSaveSuccess eliminada para evitar el toast de "Progreso guardado"
    onSaveError: (error) => {
      console.error("Error al guardar borrador:", error);

      // Extraer mensajes de error del backend
      let errorMessages: string[] = [];

      if (error?.response?.data?.message) {
        const messages = error.response.data.message;
        errorMessages = Array.isArray(messages) ? messages : [messages];
      } else if (error?.message) {
        errorMessages = [error.message];
      } else {
        errorMessages = ["Error al guardar el progreso"];
      }

      // Traducir y mostrar cada error
      errorMessages.forEach((msg, index) => {
        const translatedMsg = mensajesDeValidacion(msg);

        setTimeout(() => {
          toast.error(translatedMsg, {
            duration: 5000,
            position: "top-center",
            icon: "⚠️",
            style: {
              fontSize: "14px",
              padding: "12px 16px",
              maxWidth: "500px",
            },
          });
        }, index * 100); // Escalonar toasts si hay múltiples errores
      });
    },
  });


  const normalizeDraftEnums = (
    draft: Partial<FormData>
  ): Partial<FormData> => {
    const normalized = { ...draft };

    if (typeof normalized.tipoContrato === "string") {
      normalized.tipoContrato = normalizeContractTypeValue(
        normalized.tipoContrato
      ) as any;
    }

    if (typeof normalized.jornada === "string") {
      normalized.jornada = normalized.jornada.toLowerCase() as any;
    }

    // Regla de negocio: RH siempre captura el sueldo manualmente.
    // Aunque venga en draft/solicitud, el campo debe iniciar vacío.
    normalized.sueldoBrutoMensual = undefined as any;

    return normalized;
  };

  // Cargar borrador al iniciar
  useEffect(() => {
    const initializeForm = async () => {
      // Modo standalone: no hay candidato ni borrador, solo catálogos
      if (isStandalone) {
        try {
          setIsLoading(true);
          await loadCatalogs();
        } catch (error) {
          console.error("Error al inicializar formulario:", error);
        } finally {
          setIsInitialized(true);
          setIsLoading(false);
        }
        return;
      }

      try {
        setIsLoading(true);

        // Cargar candidato y catálogos
        await Promise.all([loadCandidate(), loadCatalogs()]);

        // Cargar borrador si existe
        const draft = await loadDraft();

        if (draft) {
          reset(normalizeDraftEnums(draft as FormData) as FormData);

          toast("Se restauró tu progreso anterior", {
            duration: 4000,
            position: "top-center",
            icon: "📋",
          });
        }

        setIsInitialized(true);
      } catch (error) {
        console.error("Error al inicializar formulario:", error);
        setIsInitialized(true);
      } finally {
        setIsLoading(false);
      }
    };

    initializeForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidateId]);

  // Actualizar puestos filtrados cuando cambie el área o se carguen los puestos
  const areaId = watch("areaId");
  useEffect(() => {
    if (areaId && puestos.length > 0) {
      const puestosFiltradosPorArea = puestos.filter(
        (puesto) => Number(puesto.areaId) === Number(areaId) && puesto.activo
      );
      setPuestosFiltrados(puestosFiltradosPorArea);
    } else {
      setPuestosFiltrados([]);
    }
  }, [areaId, puestos]);

  // Autoguardar cuando cambia formData
  useEffect(() => {
    if (currentStep < 3 && isInitialized && candidateId && formData) {
      saveDraft(formData);
    }
  }, [currentStep, formData, isInitialized, candidateId, saveDraft]);

  useEffect(() => {
    if (candidate?.proyecto?.id) {
      setSelectedProjectId(candidate.proyecto.id);
    }
  }, [candidate?.proyecto?.id]);

  const loadCatalogs = async () => {
    try {
      setLoadingCatalogs(true);

      // Cargar áreas
      try {
        const areasResponse = await fetch(
          `${
            config.apiBaseUrl
          }/catalogos/areas?page=1&limit=100`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            },
          }
        );

        if (areasResponse.ok) {
          const areasData = await areasResponse.json();
          const areasArray = Array.isArray(areasData)
            ? areasData
            : areasData.data || [];
          setAreas(areasArray.filter((area: Area) => area.activa));
        }
      } catch (err) {
        console.error("❌ Error al cargar Departamento/Áreas:", err);
      }

      // Cargar puestos
      try {
        const response = await fetch(
        `${config.apiBaseUrl}/catalogos/puestos`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('access_token')}`,
            'Content-Type': 'application/json',
          },
        },
      )

        if (response.ok) {
          const puestosData = await response.json();
          const puestosArray = Array.isArray(puestosData)
            ? puestosData
            : puestosData.data || [];
          setPuestos(puestosArray.filter((puesto: Puesto) => puesto.activo));
        }
      } catch (err) {
        console.error("❌ Error al cargar puestos:", err);
      }

      // Cargar usuarios activos
      try {
        const usuariosResponse = await fetch(
          `${
            config.apiBaseUrl
          }/users`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            },
          }
        );

        if (usuariosResponse.ok) {
          const usuariosData = await usuariosResponse.json();

          // Verificar si la respuesta es un array o tiene una propiedad data
          const usuariosArray = Array.isArray(usuariosData)
            ? usuariosData
            : usuariosData.data || [];

          // Cargar todos los usuarios activos
          const usuariosFiltrados = usuariosArray.filter(
            (user: Usuario) => user.isActive
          );

          setUsuarios(filterOutMasterUsers(usuariosFiltrados));
        }
      } catch (err) {
        console.error("❌ Error al cargar usuarios:", err);
      }

      // Cargar proyectos asignados al usuario (sin permiso adicional)
      try {
        const projectsResponse = await fetch(
          `${config.apiBaseUrl}/projects/assigned`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            },
          }
        );

        if (projectsResponse.ok) {
          const projectsData = await projectsResponse.json();
          const projectsArray = Array.isArray(projectsData)
            ? projectsData
            : projectsData.data || [];
          setProjects(projectsArray);
        }
      } catch (err) {
        console.error("❌ Error al cargar proyectos:", err);
      }
    } catch (err) {
      console.error("❌ Error general al cargar catálogos:", err);
    } finally {
      setLoadingCatalogs(false);
    }
  };

  const loadCandidate = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Cargar información real del candidato desde el API
      const candidateData = await hiringService.getCandidateById(candidateId!);

      setCandidate({
        id: candidateData.id,
        fullName: candidateData.fullName,
        email: candidateData.email,
        phone: candidateData.phone,
        age: candidateData.age,
        experienceYears: candidateData.experienceYears,
        experienceDescription: candidateData.experienceDescription,
        cvUrl: candidateData.cvUrl,
        status: candidateData.status,
        jobRequestId: candidateData.jobRequestId,
        jobRequestTipoPuesto: candidateData?.jobRequest?.tipoPuesto,
        jobRequestSalarioMinimo: candidateData?.jobRequest?.salarioMinimo,
        jobRequestSalarioMaximo: candidateData?.jobRequest?.salarioMaximo,
        proyecto: candidateData?.jobRequest?.proyecto,
      });
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error("Error al cargar candidato:", err);
      setError(
        mensajesDeValidacion(
          err.message,
          "Error al cargar información del candidato"
        )
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleAreaChange = (areaId: number) => {
    setValue("areaId", areaId);
    setValue("puestoId", 0); // Resetear puesto cuando cambia área
  };

  const validateStep = async (step: number): Promise<boolean> => {
    const fieldsToValidate: any = {
      1: [
        ...(isStandalone ? ["firstName", "lastName", "email"] : []),
        "curp",
        "rfc",
        "nss",
        "fechaNacimiento",
        "estadoCivil",
        "telefonoPersonal",
      ],
      2: [
        "areaId",
        "puestoId",
        "fechaIngreso",
        "tipoContrato",
        "jornada",
        "sueldoBrutoMensual",
      ],
    };

    return await trigger(fieldsToValidate[step]);
  };

  const handleNext = async () => {
    const isValid = await validateStep(currentStep);
    if (isValid) {
      setCurrentStep((prev) => Math.min(prev + 1, 3));
    }
  };

  const handlePrevious = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handlePrimaryAction = async () => {
    if (currentStep < 3) {
      await handleNext();
      return;
    }

    await handleFormSubmit(onSubmit)();
  };

  const onSubmit = async (data: FormData) => {
    try {
      setError(null);

      if (isStandalone) {
        // Alta standalone: crear empleado sin candidato/solicitud
        const { firstName, lastName, email, ...hireData } = data;

        const response = await hiringService.createNewEmployee({
          ...hireData,
          firstName,
          lastName,
          email,
          areaId: Number(data.areaId),
          puestoId: Number(data.puestoId),
          sueldoBrutoMensual: Number(data.sueldoBrutoMensual),
        });

        setCreatedEmployeeId(response.employee.id);
        if (response.temporaryPassword) {
          setTemporaryPassword(response.temporaryPassword);
        }
        setShowSuccessModal(true);
        return;
      }

      // Asegurar que los campos numéricos sean números
      // (excluir los campos de identidad que solo aplican en standalone)
      // Los tres campos se desestructuran solo para dejarlos fuera de hireData.
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { firstName, lastName, email, ...hireData } = data;
      const dataToSend: HireEmployeeDto = {
        ...hireData,
        areaId: Number(data.areaId),
        puestoId: Number(data.puestoId),
        sueldoBrutoMensual: Number(data.sueldoBrutoMensual),
      };

      const employee = await hiringService.hireCandidate(
        candidateId!,
        dataToSend
      );

      // Eliminar borrador del backend y localStorage
      await deleteDraft();

      // Éxito - mostrar modal con siguiente paso
      setCreatedEmployeeId(employee.id);
      setShowSuccessModal(true);
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error("Error al contratar:", err);

      // Manejar diferentes tipos de respuestas de error
      let errorMessage = "Error al contratar empleado";

      if (err.message) {
        errorMessage = err.message;
      } else if (err.error) {
        errorMessage = err.error;
      } else if (typeof err === "string") {
        errorMessage = err;
      }

      // Traducir el mensaje de error
      const translatedError = mensajesDeValidacion(errorMessage);
      setError(translatedError);

      // Scroll al top para mostrar el error
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner
          size="lg"
          text="Cargando información del candidato..."
        />
      </div>
    );
  }

  if (!isStandalone && !candidate) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="w-full max-w-none px-4 sm:px-6 lg:px-8">
          <div className="bg-red-50 border-l-4 border-red-500 rounded-r-lg p-6 shadow-sm">
            <h3 className="text-lg font-bold text-red-700 mb-2">
              Candidato no encontrado
            </h3>
            <p className="text-red-600 mb-4 leading-relaxed">
              No se pudo cargar la información del candidato
            </p>
            <button
              onClick={() => navigate("/hiring/candidates")}
              className={estiloDeBoton('primario')}
            >
              Volver a la lista
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 my-6">
      <div className="w-full max-w-none">
        {/* Header. El «Volver» va a la altura del título, no en su propio
            renglón arriba. */}
        <div className="mb-6 flex items-start gap-3">
          <button
            onClick={() =>
              navigate(isStandalone ? "/employees" : "/hiring/candidates")
            }
            className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 transition-colors mt-1.5"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver
          </button>
          <div>
            <TituloDePantalla nivel="interna">
              {isStandalone ? "Registrar nuevo empleado" : "Contratar candidato"}
            </TituloDePantalla>
            <p className="text-sm text-[#787878]">
              {isStandalone
                ? "Completa los datos para dar de alta al nuevo empleado"
                : "Completa los datos para convertir al candidato en empleado"}
            </p>
          </div>
        </div>

        {/* Información del Candidato - Solo en modo candidato */}
        {candidate && (
          <div className="bg-white border border-[#E5E7EB] rounded-lg shadow mb-6">
            <div className="px-6 py-4 border-b border-[#E5E7EB]">
              <h2 className="text-base font-semibold text-[#1E1E1F]">
                Información del candidato
              </h2>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <p className={ETIQUETA_DE_DATO}>Nombre completo:</p>
                  <p className={VALOR_DE_DATO}>{candidate.fullName}</p>
                </div>
                <div>
                  <p className={ETIQUETA_DE_DATO}>Email:</p>
                  <p className={VALOR_DE_DATO}>{candidate.email || "—"}</p>
                </div>
                <div>
                  <p className={ETIQUETA_DE_DATO}>Teléfono:</p>
                  <p className={VALOR_DE_DATO}>{candidate.phone || "—"}</p>
                </div>
                {/* El guardia anterior solo descartaba `undefined`, así que un
                    nulo se colaba y dejaba el «años» huérfano. El helper ya
                    resuelve ese caso, y además no esconde el campo: un hueco en
                    la rejilla se lee como si faltara algo. */}
                <div>
                  <p className={ETIQUETA_DE_DATO}>Experiencia:</p>
                  <p className={VALOR_DE_DATO}>
                    {experienciaDelCandidato(candidate.experienceYears)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Pestañas con el estilo de Configuración: subrayado #477FFA en la
            activa, no botones azules. */}
        <nav
          className="flex gap-1 border-b border-[#E0E0E0] mb-6"
          aria-label="Secciones del expediente"
        >
          {[
            { paso: 1, nombre: "Datos personales" },
            { paso: 2, nombre: "Datos laborales" },
            { paso: 3, nombre: "Contacto de emergencia" },
          ].map((tab) => (
            <button
              key={tab.paso}
              type="button"
              onClick={() => setCurrentStep(tab.paso)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm text-[#1E1E1F] transition-colors border-b-2 -mb-px ${
                currentStep === tab.paso
                  ? "border-[#477FFA] font-semibold"
                  : "border-transparent hover:border-gray-200"
              }`}
            >
              {tab.nombre}
            </button>
          ))}
        </nav>

        {/* Error Alert */}
        {error && (
          <div className="mb-6 bg-red-50 border-l-4 border-red-500 rounded-r-lg p-4 shadow-sm">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-bold text-red-700 text-base">
                  Error al contratar
                </h3>
                <p className="text-red-600 text-sm mt-1 leading-relaxed">
                  {error}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleFormSubmit(onSubmit)}>
          <div className="bg-white border border-[#E5E7EB] rounded-lg shadow">
            <div className="px-6 py-4 border-b border-[#E5E7EB]">
              <h2 className={TITULO_DE_SECCION}>{TITULOS_DE_PASO[currentStep]}</h2>
            </div>
            <div className="p-6">
            {/* Tab 1: Datos Personales */}
            {currentStep === 1 && (
              <div className="space-y-6">

                {/* Identidad - Solo en modo standalone (alta sin candidato) */}
                {isStandalone && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Nombre */}
                    <div>
                      <label className="block text-xs font-medium text-[#777777] mb-1.5">
                        Nombre <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        {...register("firstName", {
                          required: "El nombre es requerido",
                        })}
                        className={`w-full px-3 py-2 border rounded-md ${
                          errors.firstName
                            ? "border-red-500 focus:ring-red-500"
                            : "border-gray-300 dark:border-gray-600 focus:ring-blue-500"
                        } bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2`}
                        placeholder="Nombre(s)"
                      />
                      {errors.firstName && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.firstName.message}
                        </p>
                      )}
                    </div>

                    {/* Apellidos */}
                    <div>
                      <label className="block text-xs font-medium text-[#777777] mb-1.5">
                        Apellidos <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        {...register("lastName", {
                          required: "Los apellidos son requeridos",
                        })}
                        className={`w-full px-3 py-2 border rounded-md ${
                          errors.lastName
                            ? "border-red-500 focus:ring-red-500"
                            : "border-gray-300 dark:border-gray-600 focus:ring-blue-500"
                        } bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2`}
                        placeholder="Apellidos"
                      />
                      {errors.lastName && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.lastName.message}
                        </p>
                      )}
                    </div>

                    {/* Email */}
                    <div className="md:col-span-2">
                      <label className="block text-xs font-medium text-[#777777] mb-1.5">
                        Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        {...register("email", {
                          required: "El email es requerido",
                          pattern: {
                            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                            message: "Ingresa un email válido",
                          },
                        })}
                        className={`w-full px-3 py-2 border rounded-md ${
                          errors.email
                            ? "border-red-500 focus:ring-red-500"
                            : "border-gray-300 dark:border-gray-600 focus:ring-blue-500"
                        } bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2`}
                        placeholder="correo@empresa.com"
                      />
                      {errors.email && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.email.message}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* CURP */}
                  <div>
                    <label className="block text-xs font-medium text-[#777777] mb-1.5">
                      CURP <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        {...register("curp", {
                          ...validationRules.curp,
                          onChange: (e) => {
                            e.target.value = formatters.curp(e.target.value);
                          },
                        })}
                        maxLength={18}
                        className={`w-full px-3 py-2 pr-10 border rounded-md ${
                          errors.curp
                            ? "border-red-500 focus:ring-red-500"
                            : watch("curp")?.length === 18 && !errors.curp
                            ? "border-green-500 focus:ring-green-500"
                            : "border-gray-300 dark:border-gray-600 focus:ring-blue-500"
                        } bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2`}
                        placeholder="HEGG560427MVZRRL04"
                      />
                      {watch("curp")?.length === 18 && !errors.curp && (
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500 text-xl">
                          ✓
                        </span>
                      )}
                      {errors.curp && watch("curp")?.length > 0 && (
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500 text-xl">
                          ✗
                        </span>
                      )}
                    </div>
                    <div className="flex justify-between text-xs mt-1">
                      <span
                        className={
                          errors.curp
                            ? "text-red-500 font-medium"
                            : watch("curp")?.length === 18
                            ? "text-green-600 font-medium"
                            : "text-gray-500"
                        }
                      >
                        {errors.curp?.message ||
                          (watch("curp")?.length === 18
                            ? "✓ CURP válido"
                            : "18 caracteres alfanuméricos")}
                      </span>
                      <span
                        className={`font-medium ${
                          watch("curp")?.length === 18
                            ? "text-green-600"
                            : watch("curp")?.length > 0
                            ? "text-blue-600"
                            : "text-gray-400"
                        }`}
                      >
                        {watch("curp")?.length || 0}/18
                      </span>
                    </div>
                  </div>

                  {/* RFC */}
                  <div>
                    <label className="block text-xs font-medium text-[#777777] mb-1.5">
                      RFC <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        {...register("rfc", {
                          ...validationRules.rfc,
                          onChange: (e) => {
                            e.target.value = formatters.rfc(e.target.value);
                          },
                        })}
                        maxLength={13}
                        className={`w-full px-3 py-2 pr-10 border rounded-md ${
                          errors.rfc
                            ? "border-red-500 focus:ring-red-500"
                            : watch("rfc")?.length === 13 && !errors.rfc
                            ? "border-green-500 focus:ring-green-500"
                            : "border-gray-300 dark:border-gray-600 focus:ring-blue-500"
                        } bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2`}
                        placeholder="HEGG560427XXX"
                      />
                      {watch("rfc")?.length === 13 && !errors.rfc && (
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500 text-xl">
                          ✓
                        </span>
                      )}
                      {errors.rfc && watch("rfc")?.length > 0 && (
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500 text-xl">
                          ✗
                        </span>
                      )}
                    </div>
                    <div className="flex justify-between text-xs mt-1">
                      <span
                        className={
                          errors.rfc
                            ? "text-red-500 font-medium"
                            : watch("rfc")?.length === 13
                            ? "text-green-600 font-medium"
                            : "text-gray-500"
                        }
                      >
                        {errors.rfc?.message ||
                          (watch("rfc")?.length === 13
                            ? "✓ RFC válido"
                            : "13 caracteres alfanuméricos")}
                      </span>
                      <span
                        className={`font-medium ${
                          watch("rfc")?.length === 13
                            ? "text-green-600"
                            : watch("rfc")?.length > 0
                            ? "text-blue-600"
                            : "text-gray-400"
                        }`}
                      >
                        {watch("rfc")?.length || 0}/13
                      </span>
                    </div>
                  </div>

                  {/* NSS */}
                  <div>
                    <label className="block text-xs font-medium text-[#777777] mb-1.5">
                      NSS  <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        {...register("nss", {
                          ...validationRules.nss,
                          onChange: (e) => {
                            e.target.value = formatters.nss(e.target.value);
                          },
                        })}
                        maxLength={11}
                        className={`w-full px-3 py-2 pr-10 border rounded-md ${
                          errors.nss
                            ? "border-red-500 focus:ring-red-500"
                            : watch("nss")?.length === 11 && !errors.nss
                            ? "border-green-500 focus:ring-green-500"
                            : "border-gray-300 dark:border-gray-600 focus:ring-blue-500"
                        } bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2`}
                        placeholder="12345678901"
                      />
                      {watch("nss")?.length === 11 && !errors.nss && (
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500 text-xl">
                          ✓
                        </span>
                      )}
                      {errors.nss && (watch("nss")?.length ?? 0) > 0 && (
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500 text-xl">
                          ✗
                        </span>
                      )}
                    </div>
                    <div className="flex justify-between text-xs mt-1">
                      <span
                        className={
                          errors.nss
                            ? "text-red-500 font-medium"
                            : (watch("nss")?.length ?? 0) === 11
                            ? "text-green-600 font-medium"
                            : "text-gray-500"
                        }
                      >
                        {errors.nss?.message ||
                          ((watch("nss")?.length ?? 0) === 11
                            ? "✓ NSS válido"
                            : "11 dígitos numéricos")}
                      </span>
                      <span
                        className={`font-medium ${
                          (watch("nss")?.length ?? 0) === 11
                            ? "text-green-600"
                            : (watch("nss")?.length ?? 0) > 0
                            ? "text-blue-600"
                            : "text-gray-400"
                        }`}
                      >
                        {watch("nss")?.length ?? 0}/11
                      </span>
                    </div>
                  </div>

                  {/* Fecha de Nacimiento */}
                  <div>
                    <label className="block text-xs font-medium text-[#777777] mb-1.5">
                      Fecha de Nacimiento{" "}
                      <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      max={new Date().toISOString().split('T')[0]}
                      {...register(
                        "fechaNacimiento",
                        validationRules.fechaNacimiento
                      )}
                      className={`w-full px-3 py-2 border rounded-md ${
                        errors.fechaNacimiento
                          ? "border-red-500 focus:ring-red-500"
                          : "border-gray-300 dark:border-gray-600 focus:ring-blue-500"
                      } bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2`}
                    />
                    {errors.fechaNacimiento && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.fechaNacimiento.message}
                      </p>
                    )}
                  </div>

                  {/* Sexo */}
                  <div>
                    <label className="block text-xs font-medium text-[#777777] mb-1.5">
                      Sexo <span className="text-red-500">*</span>
                    </label>
                    <Controller
                      name="sexo"
                      control={control}
                      render={({ field }) => (
                        <SelectBuscable
                          variante="formulario"
                          opciones={SEXOS}
                          value={String(field.value ?? "")}
                          onChange={field.onChange}
                          canDelete={false}
                          placeholderDeBusqueda="Buscar sexo..."
                        />
                      )}
                    />
                  </div>

                  {/* Estado Civil */}
                  <div>
                    <label className="block text-xs font-medium text-[#777777] mb-1.5">
                      Estado Civil <span className="text-red-500">*</span>
                    </label>
                    <Controller
                      name="estadoCivil"
                      control={control}
                      rules={validationRules.estadoCivil}
                      render={({ field }) => (
                        <SelectBuscable
                          variante="formulario"
                          opciones={ESTADOS_CIVILES}
                          value={String(field.value ?? "")}
                          onChange={field.onChange}
                          error={Boolean(errors.estadoCivil)}
                          canDelete={false}
                          placeholderDeBusqueda="Buscar estado civil..."
                        />
                      )}
                    />
                    {errors.estadoCivil && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.estadoCivil.message}
                      </p>
                    )}
                  </div>

                  {/* Teléfono Personal */}
                  <div>
                    <label className="block text-xs font-medium text-[#777777] mb-1.5">
                      Teléfono Personal
                    </label>
                    <div className="relative">
                      <input
                        type="tel"
                        {...register("telefonoPersonal", {
                          ...validationRules.telefonoPersonal,
                          onChange: (e) => {
                            e.target.value = formatters.phone(e.target.value);
                          },
                        })}
                        maxLength={10}
                        className={`w-full px-3 py-2 pr-10 border rounded-md ${
                          errors.telefonoPersonal
                            ? "border-red-500 focus:ring-red-500"
                            : watch("telefonoPersonal")?.length === 10 &&
                              !errors.telefonoPersonal
                            ? "border-green-500 focus:ring-green-500"
                            : "border-gray-300 dark:border-gray-600 focus:ring-blue-500"
                        } bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2`}
                        placeholder="5551234567"
                      />
                      {watch("telefonoPersonal")?.length === 10 &&
                        !errors.telefonoPersonal && (
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500 text-xl">
                            ✓
                          </span>
                        )}
                      {errors.telefonoPersonal &&
                        (watch("telefonoPersonal")?.length ?? 0) > 0 && (
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500 text-xl">
                            ✗
                          </span>
                        )}
                    </div>
                    <div className="flex justify-between text-xs mt-1">
                      <span
                        className={
                          errors.telefonoPersonal
                            ? "text-red-500 font-medium"
                            : (watch("telefonoPersonal")?.length ?? 0) === 10
                            ? "text-green-600 font-medium"
                            : "text-gray-500"
                        }
                      >
                        {errors.telefonoPersonal?.message ||
                          ((watch("telefonoPersonal")?.length ?? 0) === 10
                            ? "✓ Teléfono válido"
                            : "10 dígitos numéricos")}
                      </span>
                      <span
                        className={`font-medium ${
                          (watch("telefonoPersonal")?.length ?? 0) === 10
                            ? "text-green-600"
                            : (watch("telefonoPersonal")?.length ?? 0) > 0
                            ? "text-blue-600"
                            : "text-gray-400"
                        }`}
                      >
                        {watch("telefonoPersonal")?.length ?? 0}/10
                      </span>
                    </div>
                  </div>
                </div>

                {/* Dirección */}
                <div>
                  <label className="block text-xs font-medium text-[#777777] mb-1.5">
                    Dirección Completa
                  </label>
                  <textarea
                    {...register("direccion")}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Calle, número, colonia, ciudad, estado, CP"
                  />
                </div>
              </div>
            )}

            {/* Tab 2: Datos Laborales */}
            {currentStep === 2 && (
              <div className="space-y-6">

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Área */}
                  <div>
                    <label className="block text-xs font-medium text-[#777777] mb-1.5">
                      Departamento/Área <span className="text-red-500">*</span>
                    </label>
                    <Controller
                      name="areaId"
                      control={control}
                      rules={validationRules.areaId}
                      render={({ field }) => (
                        <SelectBuscable
                          variante="formulario"
                          opciones={areas.map((area) => ({
                            value: String(area.id),
                            label: area.nombre,
                          }))}
                          value={field.value ? String(field.value) : ""}
                          onChange={(valor) => {
                            const nuevo = valor === "" ? 0 : Number(valor);
                            field.onChange(nuevo);
                            handleAreaChange(nuevo);
                          }}
                          placeholder="Seleccione un Departamento/Área"
                          disabled={!isStandalone}
                          error={Boolean(errors.areaId)}
                          canDelete={false}
                          placeholderDeBusqueda="Buscar área..."
                        />
                      )}
                    />
                    {errors.areaId && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.areaId.message}
                      </p>
                    )}
                    {!isStandalone && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        🔒 Campo de solo lectura
                      </p>
                    )}
                  </div>

                  {/* Puesto */}
                  <div>
                    <label className="block text-xs font-medium text-[#777777] mb-1.5">
                      Puesto <span className="text-red-500">*</span>
                    </label>
                    <Controller
                      name="puestoId"
                      control={control}
                      rules={validationRules.puestoId}
                      render={({ field }) => (
                        <SelectBuscable
                          variante="formulario"
                          opciones={(isStandalone ? puestosFiltrados : puestos).map(
                            (puesto) => ({
                              value: String(puesto.id),
                              label: puesto.nombre,
                            })
                          )}
                          value={field.value ? String(field.value) : ""}
                          onChange={(valor) =>
                            field.onChange(valor === "" ? 0 : Number(valor))
                          }
                          placeholder="Seleccione un puesto"
                          disabled={!isStandalone || !areaId}
                          error={Boolean(errors.puestoId)}
                          canDelete={false}
                          placeholderDeBusqueda="Buscar puesto..."
                        />
                      )}
                    />
                    {errors.puestoId && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.puestoId.message}
                      </p>
                    )}
                    {!isStandalone && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        🔒 Campo de solo lectura
                      </p>
                    )}
                    {isStandalone && !areaId && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Selecciona primero un Departamento/Área
                      </p>
                    )}
                  </div>

                  {/* Fecha de Ingreso */}
                  <div>
                    <label className="block text-xs font-medium text-[#777777] mb-1.5">
                      Fecha de Ingreso <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      {...register(
                        "fechaIngreso",
                        validationRules.fechaIngreso
                      )}
                      className={`w-full px-3 py-2 border rounded-md ${
                        errors.fechaIngreso
                          ? "border-red-500 focus:ring-red-500"
                          : "border-gray-300 dark:border-gray-600 focus:ring-blue-500"
                      } bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2`}
                    />
                    {errors.fechaIngreso && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.fechaIngreso.message}
                      </p>
                    )}
                  </div>

                  {/* Tipo de Contrato */}
                  <div>
                    <label className="block text-xs font-medium text-[#777777] mb-1.5">
                      Tipo de Contrato <span className="text-red-500">*</span>
                    </label>
                    <Controller
                      name="tipoContrato"
                      control={control}
                      rules={validationRules.tipoContrato}
                      render={({ field }) => (
                        <SelectBuscable
                          variante="formulario"
                          opciones={availableContractTypes}
                          value={String(field.value ?? "")}
                          onChange={field.onChange}
                          error={Boolean(errors.tipoContrato)}
                          canDelete={false}
                          placeholderDeBusqueda="Buscar tipo de contrato..."
                        />
                      )}
                    />
                    {errors.tipoContrato && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.tipoContrato.message}
                      </p>
                    )}
                  </div>

                  {/* Jornada */}
                  <div>
                    <label className="block text-xs font-medium text-[#777777] mb-1.5">
                      Jornada <span className="text-red-500">*</span>
                    </label>
                    <Controller
                      name="jornada"
                      control={control}
                      rules={validationRules.jornada}
                      render={({ field }) => (
                        <SelectBuscable
                          variante="formulario"
                          opciones={JORNADAS}
                          value={String(field.value ?? "")}
                          onChange={field.onChange}
                          error={Boolean(errors.jornada)}
                          canDelete={false}
                          placeholderDeBusqueda="Buscar jornada..."
                        />
                      )}
                    />
                    {errors.jornada && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.jornada.message}
                      </p>
                    )}
                  </div>

                  {/* Sueldo Bruto Mensual */}
                  <div>
                    <label className="block text-xs font-medium text-[#777777] mb-1.5">
                      Sueldo Bruto Mensual{" "}
                      <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      {...register(
                        "sueldoBrutoMensual",
                        validationRules.sueldoBrutoMensual
                      )}
                      min="0"
                      step="0.01"
                      className={`w-full px-3 py-2 border rounded-md ${
                        errors.sueldoBrutoMensual
                          ? "border-red-500 focus:ring-red-500"
                          : "border-gray-300 dark:border-gray-600 focus:ring-blue-500"
                      } bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2`}
                      placeholder="00.00"
                    />
                    {errors.sueldoBrutoMensual && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.sueldoBrutoMensual.message}
                      </p>
                    )}
                    {approvedSalaryRangeText && (
                      <p className="text-xs text-gray-500 mt-1">
                        {approvedSalaryRangeText}
                      </p>
                    )}
                  </div>

                  {/* Jefe Inmediato */}
                  <div>
                    <label className="block text-xs font-medium text-[#777777] mb-1.5">
                      Jefe Inmediato {loadingCatalogs && "(Cargando...)"}
                    </label>
                    <Controller
                      name="jefeInmediatoId"
                      control={control}
                      render={({ field }) => (
                        <SelectBuscable
                          variante="formulario"
                          opciones={[
                            { value: "", label: "Sin jefe asignado" },
                            ...usuarios.map((usuario) => ({
                              value: usuario.id,
                              label: `${usuario.firstName} ${usuario.lastName} - ${usuario.email}`,
                            })),
                          ]}
                          value={String(field.value ?? "")}
                          onChange={field.onChange}
                          disabled={loadingCatalogs}
                          canDelete={false}
                          placeholderDeBusqueda="Buscar usuario..."
                        />
                      )}
                    />
                    {!loadingCatalogs && (
                      <p className="text-xs text-gray-500 mt-1">
                        {usuarios.length} usuario(s) disponible(s)
                      </p>
                    )}
                  </div>
                </div>

                {mostrarDatosProyecto && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 ">
                    <div className="col-span-3">
                      <label className="text-lg font-semibold text-gray-500 dark:text-gray-400">
                        Datos de proyecto
                      </label>
                    </div>
                    <div className="col-span-3">
                      <label className="block text-xs font-medium text-[#777777] mb-1.5">
                        Proyecto
                      </label>
                      {proyectoEsDeSolicitud ? (
                        <>
                          <input
                            type="text"
                            value={proyectoDeLaSolicitud?.name ?? ""}
                            readOnly
                            className="w-full md:w-[420px] px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-400 cursor-not-allowed"
                          />
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            🔒 Campo de solo lectura
                          </p>
                        </>
                      ) : (
                        <SelectBuscable
                          variante="formulario"
                          opciones={projects.map((project) => ({
                            value: String(project.id),
                            label: project.name,
                          }))}
                          value={selectedProjectId}
                          onChange={setSelectedProjectId}
                          placeholder="Selecciona un proyecto"
                          className="w-full md:w-[420px]"
                          placeholderDeBusqueda="Buscar proyecto..."
                        />
                      )}
                    </div>
                    <div className="relative group max-w-[160px]">
                      <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        Nombre:
                      </label>
                      <p className="text-base font-semibold text-gray-900 dark:text-white truncate">
                        {selectedProject?.name ?? ""}
                      </p>
                      <span className="absolute left-0 bottom-full mb-1 hidden group-hover:block whitespace-nowrap bg-black text-white text-xs py-1 px-2 rounded shadow-lg z-50">
                        {selectedProject?.name ?? ""}
                      </span>
                    </div>
                    <div className="relative group max-w-[160px]">
                      <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        Descripción:
                      </label>
                      <p className="text-base font-semibold text-gray-900 dark:text-white truncate">
                        {selectedProject?.description ?? ""}
                      </p>
                      <span className="absolute left-0 bottom-full mb-1 hidden group-hover:block whitespace-nowrap bg-black text-white text-xs py-1 px-2 rounded shadow-lg z-50">
                        {selectedProject?.description ?? ""}
                      </span>
                    </div>
                    <div className="relative group max-w-[160px]">
                      <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        Fecha de creación:
                      </label>
                      <p className="text-base font-semibold text-gray-900 dark:text-white truncate">
                        {selectedProject?.createdAt
                          ? formatDate(selectedProject.createdAt)
                          : ""}
                      </p>
                      <span className="absolute left-0 bottom-full mb-1 hidden group-hover:block whitespace-nowrap bg-black text-white text-xs py-1 px-2 rounded shadow-lg z-50">
                        {selectedProject?.createdAt
                          ? formatDate(selectedProject.createdAt)
                          : ""}
                      </span>
                    </div>
                  </div>
                )}

                {/* Prestaciones */}
                <div>
                  <label className="block text-xs font-medium text-[#777777] mb-1.5">
                    Prestaciones
                  </label>
                  <textarea
                    {...register("prestaciones")}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Aguinaldo, vacaciones, prima vacacional, seguro médico, etc."
                  />
                </div>
              </div>
            )}

            {/* Tab 3: Contacto de Emergencia */}
            {currentStep === 3 && (
              <div className="space-y-6">

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Nombre */}
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-[#777777] mb-1.5">
                      Nombre Completo
                    </label>
                    <input
                      type="text"
                      {...register("emergenciaContactoNombre")}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Nombre del contacto de emergencia"
                    />
                  </div>

                  {/* Relación */}
                  <div>
                    <label className="block text-xs font-medium text-[#777777] mb-1.5">
                      Relación
                    </label>
                    <input
                      type="text"
                      {...register("emergenciaContactoRelacion")}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Padre, Madre, Esposo/a, etc."
                    />
                  </div>

                  {/* Teléfono */}
                  <div>
                    <label className="block text-xs font-medium text-[#777777] mb-1.5">
                      Teléfono
                    </label>
                    <div className="relative">
                      <input
                        type="tel"
                        {...register("emergenciaContactoTelefono", {
                          ...validationRules.emergenciaContactoTelefono,
                          onChange: (e) => {
                            e.target.value = formatters.phone(e.target.value);
                          },
                        })}
                        maxLength={10}
                        className={`w-full px-3 py-2 pr-10 border rounded-md ${
                          errors.emergenciaContactoTelefono
                            ? "border-red-500 focus:ring-red-500"
                            : watch("emergenciaContactoTelefono")?.length ===
                                10 && !errors.emergenciaContactoTelefono
                            ? "border-green-500 focus:ring-green-500"
                            : "border-gray-300 dark:border-gray-600 focus:ring-blue-500"
                        } bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2`}
                        placeholder="5551234567"
                      />
                      {watch("emergenciaContactoTelefono")?.length === 10 &&
                        !errors.emergenciaContactoTelefono && (
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500 text-xl">
                            ✓
                          </span>
                        )}
                      {errors.emergenciaContactoTelefono &&
                        (watch("emergenciaContactoTelefono")?.length ?? 0) >
                          0 && (
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500 text-xl">
                            ✗
                          </span>
                        )}
                    </div>
                    <div className="flex justify-between text-xs mt-1">
                      <span
                        className={
                          errors.emergenciaContactoTelefono
                            ? "text-red-500 font-medium"
                            : (watch("emergenciaContactoTelefono")?.length ??
                                0) === 10
                            ? "text-green-600 font-medium"
                            : "text-gray-500"
                        }
                      >
                        {errors.emergenciaContactoTelefono?.message ||
                          ((watch("emergenciaContactoTelefono")?.length ??
                            0) === 10
                            ? "✓ Teléfono válido"
                            : "10 dígitos numéricos")}
                      </span>
                      <span
                        className={`font-medium ${
                          (watch("emergenciaContactoTelefono")?.length ?? 0) ===
                          10
                            ? "text-green-600"
                            : (watch("emergenciaContactoTelefono")?.length ??
                                0) > 0
                            ? "text-blue-600"
                            : "text-gray-400"
                        }`}
                      >
                        {watch("emergenciaContactoTelefono")?.length ?? 0}/10
                      </span>
                    </div>
                  </div>
                </div>

                {/* Resumen Final */}
                <div className="mt-8 bg-green-50 border-l-4 border-green-500 rounded-r-lg p-6 shadow-sm">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-green-600 mt-0.5" />
                    <div>
                      <h3 className="font-bold text-green-700 mb-2">
                        Listo para contratar
                      </h3>
                      <p className="text-green-600 font-semibold text-sm leading-relaxed">
                        Has completado todos los pasos. Revisa la información y
                        haz clic en "Crear Expediente" para finalizar el
                        proceso.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            </div>
          </div>

          {/* Botones de avance. Van fuera de la tarjeta: son el envío del
              formulario, no un campo más de la sección. */}
          <div className="mt-6 flex items-center justify-between">
              <button
                type="button"
                onClick={handlePrevious}
                disabled={currentStep === 1}
                className={estiloDeBoton('secundario')}
              >
                <ArrowLeft className="w-4 h-4" />
                Anterior
              </button>

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() =>
                    navigate(isStandalone ? "/employees" : "/hiring/candidates")
                  }
                  className={estiloDeBoton('secundario')}
                >
                  Cancelar
                </button>

                <button
                  type="button"
                  onClick={handlePrimaryAction}
                  disabled={isSubmitting}
                  className={estiloDeBoton('primario')}
                >
                  {currentStep < 3 ? (
                    <>
                      Siguiente
                      <ArrowRight className="w-4 h-4" />
                    </>
                  ) : isSubmitting ? (
                    <>
                      <LoadingSpinner size="sm" />
                      Creando expediente...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      Crear Expediente
                    </>
                  )}
                </button>
              </div>
            </div>
        </form>

        <Modal
          isOpen={showSuccessModal}
          onClose={() => setShowSuccessModal(false)}
          title="Expediente creado"
          closeOnOutsideClick={false}
          showCloseButton={false}
          footer={
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => {
                  setShowSuccessModal(false);
                  if (createdEmployeeId) {
                    navigate(`/employees/${createdEmployeeId}?tab=Expediente`);
                  } else {
                    navigate("/employees");
                  }
                }}
                className={estiloDeBoton('primario')}
              >
                Aceptar
              </button>
            </div>
          }
        >
          <p className="text-sm text-gray-600">
            El expediente fue creado. El empleado ahora aparece en Empleados
            {" > "}
            Expedientes.
          </p>
          {temporaryPassword && (
            <div className="mt-4 bg-yellow-50 border-l-4 border-yellow-400 rounded-r-md p-4">
              <p className="text-sm font-semibold text-yellow-800 mb-1">
                Contraseña temporal
              </p>
              <p className="text-xs text-yellow-700 mb-2">
                Comparte esta contraseña con el empleado para su primer acceso.
                No volverá a mostrarse.
              </p>
              <code className="block text-base font-mono font-bold text-gray-900 bg-white border border-yellow-300 rounded px-3 py-2 select-all">
                {temporaryPassword}
              </code>
            </div>
          )}
        </Modal>
      </div>
    </div>
  );
};
